/**
 * @fileoverview Link/Tile service for managing bookmark-style links
 *
 * This service handles all operations related to link tiles (bookmarks) in the
 * application. Link tiles are user-created shortcuts to external websites and
 * services that can be organized into groups.
 *
 * Features:
 * - CRUD operations for link tiles
 * - Group association management
 * - URL validation and protocol handling
 * - Data normalization for consistent UI display
 * - Owner email resolution
 *
 * Data Flow:
 *   UI Component -> LinkService -> API Client -> Backend API
 *   Backend API -> API Client -> LinkService -> UI Component
 */

import { apiClient } from "./api";
import { API_ENDPOINTS } from "../constants/index";
import { ensureProtocol } from "../utils/urlUtils";
import {
  normalizeTile,
  type NormalizedTile,
  type RawTile,
} from "../utils/dataUtils";
import { cache, CACHE_KEYS, withCache } from "../utils/cacheUtils";
import { logger } from "@common/utils/logger";

// Type definitions

/** A link as the dialogs edit it: `LinkTileBase`, with the legacy aliases. */
export interface CreateLinkData {
  tile_name?: string;
  name?: string | null;
  title?: string | null;
  tile_url?: string;
  url?: string;
  description?: string | null;
  emoji?: string | null;
  imgName?: string | null;
  bgColor?: string | null;
  group_id?: number | null;
}

/** The fields of a link to change (PATCH /links/{id}). */
export type UpdateLinkData = Omit<CreateLinkData, "group_id">;

export interface GroupLinkAssociation {
  groupId: number;
  tileId: number | string;
}

/**
 * Service class for link/tile operations providing CRUD functionality
 * and business logic for bookmark-style links.
 *
 * This service abstracts the complexity of link management, including
 * URL validation, data normalization, and group associations.
 */
export class LinkService {
  /**
   * Get all links (with caching)
   */
  async getLinks(
    onFreshData?: (data: NormalizedTile[]) => void,
  ): Promise<NormalizedTile[]> {
    return withCache(
      CACHE_KEYS.LINKS,
      async () => {
        const links = await apiClient.get<RawTile[]>(API_ENDPOINTS.LINKS);
        const linkArray = Array.isArray(links) ? links : [];
        return linkArray.map(normalizeTile);
      },
      {
        ttl: 5 * 60 * 1000, // 5 minutes cache
        onCacheHit: () => logger.debug("[LinkService] Using cached links"),
        onCacheMiss: () => logger.debug("[LinkService] Fetching fresh links"),
        onFreshData,
      },
    );
  }

  /**
   * Get links for a specific group (with caching)
   */
  async getLinksByGroup(
    groupId: number,
    onFreshData?: (data: NormalizedTile[]) => void,
  ): Promise<NormalizedTile[]> {
    return withCache(
      CACHE_KEYS.GROUP_LINKS(groupId),
      async () => {
        const links = await apiClient.get<RawTile[]>(
          `${API_ENDPOINTS.GROUPS}${groupId}/links`,
        );
        return links.map(normalizeTile);
      },
      {
        ttl: 5 * 60 * 1000, // 5 minutes cache
        onCacheHit: () =>
          logger.debug(`[LinkService] Using cached links for group ${groupId}`),
        onCacheMiss: () =>
          logger.debug(
            `[LinkService] Fetching fresh links for group ${groupId}`,
          ),
        onFreshData,
      },
    );
  }

  /**
   * Create a new link
   */
  async createLink(
    linkData: CreateLinkData,
    userEmail: string,
  ): Promise<NormalizedTile> {
    logger.error("[LinkService.createLink] START - Input data:", {
      linkData,
      userEmail,
    });

    // Remove group_id and frontend-specific fields from the data
    const { group_id, tile_url, tile_name, ...cleanLinkData } = linkData;

    // Get URL and name from multiple possible fields
    const url = tile_url || linkData.url || "";
    const name = tile_name || linkData.name || "";

    const processedData = {
      ...cleanLinkData,
      url: ensureProtocol(url),
      name,
      owner_email: userEmail,
    };

    logger.error(
      "[LinkService.createLink] About to call apiClient.post with:",
      {
        endpoint: API_ENDPOINTS.LINKS,
        processedData,
      },
    );

    const newLink = await apiClient.post<RawTile>(
      API_ENDPOINTS.LINKS,
      processedData,
    );

    // Invalidate cache after creating
    cache.remove(CACHE_KEYS.LINKS);

    logger.error(
      "[LinkService.createLink] POST completed, response:",
      newLink,
    );

    return normalizeTile(newLink);
  }

  /**
   * Update an existing link
   */
  async updateLink(
    linkId: number | string,
    linkData: UpdateLinkData,
    userEmail: string,
  ): Promise<NormalizedTile> {
    // Get URL from multiple possible fields
    const url = linkData.tile_url || linkData.url || "";

    const processedData = {
      ...linkData,
      url: ensureProtocol(url),
      owner_email: userEmail,
    };

    const updatedLink = await apiClient.patch<RawTile>(
      `${API_ENDPOINTS.LINKS}${linkId}`,
      processedData,
    );

    // Invalidate cache after updating
    cache.remove(CACHE_KEYS.LINKS);
    // Also clear any group-specific caches (we don't know which groups this link belongs to)
    logger.debug(`[LinkService] Invalidating caches after update`);

    return normalizeTile(updatedLink);
  }

  /**
   * Delete a link
   */
  async deleteLink(linkId: number | string): Promise<number | string> {
    await apiClient.delete(`${API_ENDPOINTS.LINKS}${linkId}`);

    // Invalidate cache after deleting
    cache.remove(CACHE_KEYS.LINKS);
    logger.debug(`[LinkService] Invalidating caches after delete`);

    return linkId;
  }

  /**
   * Link a tile to a group
   */
  async linkToGroup(
    groupId: number,
    tileId: number | string,
  ): Promise<GroupLinkAssociation> {
    await apiClient.post(`${API_ENDPOINTS.GROUPS}${groupId}/links/${tileId}`);
    return { groupId, tileId };
  }

  /**
   * Unlink a tile from a group
   */
  async unlinkFromGroup(
    groupId: number,
    tileId: number | string,
  ): Promise<GroupLinkAssociation> {
    await apiClient.delete(`${API_ENDPOINTS.GROUPS}${groupId}/links/${tileId}`);
    return { groupId, tileId };
  }
}

// Create singleton instance
export const linkService = new LinkService();
